import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import config from "@/config";
import { useAuth } from "./AuthContext";
import { exchangeCode } from "./api";

// Cognito authorization codes are opaque UUID-ish strings — strip
// anything outside [a-f0-9-] as a belt-and-suspenders measure.
function sanitizeAuthCode(raw: string): string | null {
  const cleaned = raw.replace(/[^a-f0-9-]/g, "");
  return cleaned.length >= 30 ? cleaned : null;
}

export default function LoginPage() {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [exchanging, setExchanging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const exchangeStarted = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const sanitized = code ? sanitizeAuthCode(code) : null;

    if (sanitized && !exchangeStarted.current) {
      exchangeStarted.current = true;
      setExchanging(true);
      // Strip the code from the URL so a back-button refresh doesn't
      // re-exchange (Cognito codes are single-use).
      window.history.replaceState({}, "", window.location.pathname);

      exchangeCode(sanitized, "/login")
        .then((authUser) => {
          setUser(authUser);
          const from =
            (location.state as { from?: { pathname?: string } } | null)?.from
              ?.pathname || "/";
          navigate(from, { replace: true });
        })
        .catch((err) => {
          console.error("Authorization code exchange failed:", err);
          setError("Authentication failed. Please try again.");
          exchangeStarted.current = false;
          setExchanging(false);
        });
      return;
    }

    if (!loading && user) {
      const from =
        (location.state as { from?: { pathname?: string } } | null)?.from
          ?.pathname || "/";
      navigate(from, { replace: true });
      return;
    }

    if (
      !loading &&
      !user &&
      !code &&
      !exchanging &&
      !error &&
      config.cognitoDomain &&
      config.cognitoClientId
    ) {
      const redirectUri = encodeURIComponent(
        window.location.origin + "/login"
      );
      const url =
        `https://${config.cognitoDomain}/oauth2/authorize` +
        `?client_id=${config.cognitoClientId}` +
        `&response_type=code` +
        `&redirect_uri=${redirectUri}` +
        `&scope=${config.cognitoScopes}`;
      window.location.href = url;
    }
  }, [loading, user, exchanging, error, setUser, navigate, location]);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        flexDirection: "column",
        gap: 2,
      }}
    >
      {error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <>
          <CircularProgress />
          <Typography color="text.secondary">
            {exchanging ? "Signing you in…" : "Redirecting to sign-in…"}
          </Typography>
        </>
      )}
    </Box>
  );
}
